/**
 * Role and organization-status constants.
 *
 * These four role strings were previously hardcoded in six or more places
 * (UserFormModal, AdminUserManagement's filter/bulk/tone maps, AuthContext, App's
 * route guards, RootLayout's badge). Organization statuses add three more, so they
 * live here rather than being retyped per component.
 *
 * Keep in sync with UserRole in backend/app/models/db_models.py.
 */

export const ROLES = {
  GLOBAL_ADMIN: 'global_admin',
  ADMIN: 'admin',
  RECRUITER: 'recruiter',
  CANDIDATE: 'candidate',
};

/** Roles an org admin can assign inside their own org. */
export const ASSIGNABLE_ROLES = [ROLES.ADMIN, ROLES.RECRUITER, ROLES.CANDIDATE];

/**
 * Roles an invite can carry. Candidates are not invited into an org -- they arrive
 * through a hiring flow -- and global_admin is never invitable at all: the backend
 * request schema rejects it and the accept path checks again.
 */
export const INVITABLE_ROLES = [ROLES.ADMIN, ROLES.RECRUITER];

export const ROLE_LABELS = {
  [ROLES.GLOBAL_ADMIN]: 'Platform Admin',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.RECRUITER]: 'Recruiter',
  [ROLES.CANDIDATE]: 'Candidate',
};

export const ROLE_OPTIONS = ASSIGNABLE_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

export const INVITE_ROLE_OPTIONS = INVITABLE_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

export const roleLabel = (role) => ROLE_LABELS[role] || role || '—';

/**
 * Organization status. Derived server-side from tenant kind plus admin count -- never
 * stored, because a stored copy drifts the moment an admin is deactivated.
 *
 *   pending  a real company org with no admin yet; awaiting appointment
 *   active   a real company org with at least one admin
 *   solo     one person on a free mailbox; no member admin by design
 *   system   the platform org; never appointable
 *   legacy   a pre-per-domain tenant, awaiting the split
 */
export const ORG_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',
  SOLO: 'solo',
  SYSTEM: 'system',
  LEGACY: 'legacy',
};

export const ORG_STATUS_LABELS = {
  [ORG_STATUS.ACTIVE]: 'Active',
  [ORG_STATUS.PENDING]: 'Needs admin',
  [ORG_STATUS.SOLO]: 'Solo',
  [ORG_STATUS.SYSTEM]: 'Platform',
  [ORG_STATUS.LEGACY]: 'Legacy',
};

/** Tone keys for the status chip; consumed by the dashboard's badge component. */
export const ORG_STATUS_TONE = {
  [ORG_STATUS.ACTIVE]: 'success',
  [ORG_STATUS.PENDING]: 'warning',
  [ORG_STATUS.SOLO]: 'neutral',
  [ORG_STATUS.SYSTEM]: 'info',
  [ORG_STATUS.LEGACY]: 'danger',
};

export const INVITE_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REVOKED: 'revoked',
  EXPIRED: 'expired',
};

export const INVITE_STATUS_LABELS = {
  [INVITE_STATUS.PENDING]: 'Pending',
  [INVITE_STATUS.ACCEPTED]: 'Accepted',
  [INVITE_STATUS.REVOKED]: 'Revoked',
  [INVITE_STATUS.EXPIRED]: 'Expired',
};
